import { useState, useRef, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { DetailSection } from '../ui/DetailSection.js';
import { KVGrid } from '../ui/KVGrid.js';
import { KVRow } from '../ui/KVRow.js';
import { Dropdown, type DropdownOption } from '../ui/Dropdown.js';
import { DatePicker } from '../ui/DatePicker.js';
import { Textarea } from '../ui/Textarea.js';
import { Input } from '../ui/Input.js';
import { Button } from '../ui/Button.js';
import { Pill } from '../PillGroup.js';
import { IconButton } from '../ui/IconButton.js';
import { FileUploadZone } from '../ui/FileUploadZone.js';
import { useToast } from '../ui/Toast.js';
import {
  useSaveBrief,
  useSkipBrief,
  useDeleteAsset,
  useGenerateContentPlan,
  type ProjectRow,
} from '../../hooks/useProjects.js';
import { useComposeBriefEmail, useClientInteractions, type ClientInteraction } from '../../hooks/useProjectOutreach.js';

// ── Types ──────────────────────────────────────────────────────────────────────

interface BriefData {
  businessName?: string;
  industry?: string;
  audience?: string;
  valueProposition?: string;
  tone?: string;
  colors?: { text?: string; attachments?: string[] };
  typography?: string;
  references?: { links?: string[]; attachments?: string[] };
  logo?: string | null;
  images?: string[];
  cta?: { type?: string; custom?: string | null };
  desiredDeadline?: string;
  notes?: string;
  skipped?: boolean;
}

function parseBriefData(raw: string | null): BriefData {
  if (!raw) return {};
  try { return JSON.parse(raw) as BriefData; } catch { return {}; }
}

const PRESET_TONES = ['formal', 'casual', 'luxury', 'friendly', 'bold', 'minimalist'];

// ── FieldRow — mirrors KVRow visual layout for complex controls ────────────────

function FieldRow({ label, children, last = false }: { label: string; children: ReactNode; last?: boolean }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 'var(--evo-space-2)',
      padding: 'var(--evo-detail-kv-row-py) 0',
      borderBottom: last ? undefined : '1px solid var(--evo-stroke)',
      minHeight: 'var(--evo-detail-kv-row-min-h)',
    }}>
      <span style={{
        width: 'var(--evo-detail-kv-label-w)',
        flexShrink: 0,
        paddingTop: 6,
        fontSize: 'var(--evo-fs-label)',
        color: 'var(--evo-text-muted)',
        fontFamily: 'var(--evo-font)',
      }}>
        {label}
      </span>
      <div style={{ flex: 1, minWidth: 0, paddingTop: 4 }}>
        {children}
      </div>
    </div>
  );
}

// ── BriefSection ──────────────────────────────────────────────────────────────

export function BriefSection({ project, divider = true, onGenerated, onSendBrief }: { project: ProjectRow; divider?: boolean; onGenerated?: () => void; onSendBrief?: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const saveBriefMut = useSaveBrief();
  const skipBriefMut = useSkipBrief();
  const deleteAssetMut = useDeleteAsset();
  const generateMut = useGenerateContentPlan();
  const sendBriefMut = useComposeBriefEmail(project.id);
  const { data: clientInteractions } = useClientInteractions(project.id);
  const latestBriefResponse = clientInteractions?.find((i: ClientInteraction) => i.stage === 'brief' && i.status === 'responded') ?? null;

  const initialBrief = useMemo(() => parseBriefData(project.briefData), [project.briefData]);
  const briefRef = useRef<BriefData>(initialBrief);
  const [brief, setBriefState] = useState<BriefData>(initialBrief);

  const [newLink, setNewLink] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);
  const [deadlineError, setDeadlineError] = useState<string | null>(null);
  const [showCustomTone, setShowCustomTone] = useState(
    !!initialBrief.tone && !PRESET_TONES.includes(initialBrief.tone),
  );

  const minDeadline = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 3);
    return d;
  }, []);

  const uploadUrl = `/api/projects/${project.id}/assets`;

  // ── State helpers ──────────────────────────────────────────────────────────

  function patch(updates: Partial<BriefData>) {
    const next = { ...briefRef.current, ...updates };
    briefRef.current = next;
    setBriefState(next);
  }

  async function autoSave(updates: Partial<BriefData>) {
    const next = { ...briefRef.current, ...updates };
    briefRef.current = next;
    setBriefState(next);
    await saveBriefMut.mutateAsync({ id: project.id, briefData: next });
  }

  async function serverSave() {
    await saveBriefMut.mutateAsync({ id: project.id, briefData: briefRef.current });
  }

  // ── Asset delete ───────────────────────────────────────────────────────────

  async function handleDeleteAsset(url: string, field: 'logo' | 'images' | 'colors' | 'references') {
    await deleteAssetMut.mutateAsync({ projectId: project.id, path: url });
    const b = briefRef.current;
    let updates: Partial<BriefData> = {};
    if (field === 'logo')       updates = { logo: null };
    else if (field === 'images') updates = { images: (b.images ?? []).filter(p => p !== url) };
    else if (field === 'colors') updates = { colors: { ...b.colors, attachments: (b.colors?.attachments ?? []).filter(p => p !== url) } };
    else                         updates = { references: { ...b.references, attachments: (b.references?.attachments ?? []).filter(p => p !== url) } };
    await autoSave(updates);
  }

  // ── URL list ───────────────────────────────────────────────────────────────

  function addLink() {
    const url = newLink.trim();
    if (!url) return;
    try { new URL(url); } catch {
      setLinkError(t('validation.invalidUrl', { defaultValue: 'URL inválida' }));
      return;
    }
    setLinkError(null);
    const links = [...(briefRef.current.references?.links ?? []), url];
    void autoSave({ references: { ...briefRef.current.references, links } });
    setNewLink('');
  }

  function removeLink(url: string) {
    const links = (briefRef.current.references?.links ?? []).filter(l => l !== url);
    void autoSave({ references: { ...briefRef.current.references, links } });
  }

  // ── Deadline ───────────────────────────────────────────────────────────────

  function handleDeadlineChange(d: Date | null) {
    if (!d) {
      setDeadlineError(null);
      const { desiredDeadline: _, ...rest } = briefRef.current;
      briefRef.current = rest;
      setBriefState(rest);
      void saveBriefMut.mutateAsync({ id: project.id, briefData: rest });
      return;
    }
    if (d < minDeadline) { setDeadlineError(t('projects.brief.deadlineMinError')); return; }
    setDeadlineError(null);
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    void autoSave({ desiredDeadline: iso });
  }

  // ── Footer actions ─────────────────────────────────────────────────────────

  function validateForDesign(): string | null {
    const b = briefRef.current;
    if (!b.businessName?.trim()) return t('projects.brief.validation.missingBusinessName');
    if (!b.cta?.type)            return t('projects.brief.validation.missingCta');
    if (!b.desiredDeadline)      return t('projects.brief.validation.missingDeadline');
    const min = new Date(); min.setDate(min.getDate() + 3);
    if (new Date(b.desiredDeadline) < min) return t('projects.brief.deadlineMinError');
    return null;
  }

  async function handleSaveDraft() {
    try {
      await serverSave();
      toast.success(t('projects.brief.actions.saveDraft'));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al guardar');
    }
  }

  async function handleSkipBrief() {
    try {
      await skipBriefMut.mutateAsync(project.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error');
    }
  }

  async function handleGenerateContentPlan() {
    const err = validateForDesign();
    if (err) { toast.error(err); return; }
    try {
      await saveBriefMut.mutateAsync({ id: project.id, briefData: briefRef.current, transitionToBriefed: true });
      await generateMut.mutateAsync(project.id);
      onGenerated?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error');
    }
  }

  async function handleRefillBrief() {
    const { skipped: _, ...rest } = briefRef.current;
    try {
      await autoSave({ ...rest });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error');
    }
  }

  // ── Dropdown options ───────────────────────────────────────────────────────

  const toneOptions: DropdownOption<string>[] = [
    { value: 'formal',      label: t('projects.brief.tones.formal') },
    { value: 'casual',      label: t('projects.brief.tones.casual') },
    { value: 'luxury',      label: t('projects.brief.tones.luxury') },
    { value: 'friendly',    label: t('projects.brief.tones.friendly') },
    { value: 'bold',        label: t('projects.brief.tones.bold') },
    { value: 'minimalist',  label: t('projects.brief.tones.minimalist') },
    { value: 'custom',      label: t('projects.brief.tones.custom') },
  ];

  const ctaOptions: DropdownOption<string>[] = [
    { value: 'form',        label: t('projects.brief.cta.types.form') },
    { value: 'appointment', label: t('projects.brief.cta.types.appointment') },
    { value: 'purchase',    label: t('projects.brief.cta.types.purchase') },
    { value: 'call',        label: t('projects.brief.cta.types.call') },
    { value: 'download',    label: t('projects.brief.cta.types.download') },
    { value: 'subscribe',   label: t('projects.brief.cta.types.subscribe') },
    { value: 'other',       label: t('projects.brief.cta.types.other') },
  ];

  const toneIsPreset       = PRESET_TONES.includes(brief.tone ?? '');
  const toneDropdownValue  = toneIsPreset ? (brief.tone ?? null) : (brief.tone != null ? 'custom' : null);
  const isSaving           = saveBriefMut.isPending || skipBriefMut.isPending || generateMut.isPending;

  // ── Skipped state ──────────────────────────────────────────────────────────

  if (brief.skipped) {
    return (
      <DetailSection title={t('projects.brief.title')} divider={divider}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-3)', padding: 'var(--evo-space-3) 0' }}>
          <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-text-muted)', fontFamily: 'var(--evo-font)' }}>
            {t('projects.brief.skipped')}
          </span>
          <Button variant="ghost" tone="cyan" size="sm" loading={isSaving} onClick={() => { void handleRefillBrief(); }}>
            {t('projects.brief.refillBrief')}
          </Button>
        </div>
      </DetailSection>
    );
  }

  // ── Full form ──────────────────────────────────────────────────────────────

  return (
    <DetailSection title={t('projects.brief.title')} divider={divider}>
      {latestBriefResponse !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', marginBottom: 'var(--evo-space-4)' }}>
          <Pill variant="outline" tone="cyan" size="sm">{t('projects.brief.clientResponded')}</Pill>
          {latestBriefResponse.respondedAt !== null && (
            <span style={{ fontSize: 'var(--evo-fs-xs)', opacity: 0.6 }}>
              {new Date(latestBriefResponse.respondedAt).toLocaleDateString()}
            </span>
          )}
        </div>
      )}
      {/* Rows 1-2: text fields — 2 columns */}
      <KVGrid columns={2}>
        <KVRow
          label={t('projects.brief.businessName')}
          value={brief.businessName ?? ''}
          placeholder="—"
          onSave={async v => { await autoSave({ businessName: v }); }}
        />
        <KVRow
          label={t('projects.brief.industry')}
          value={brief.industry ?? ''}
          placeholder="—"
          onSave={async v => { await autoSave({ industry: v }); }}
        />
        <KVRow
          label={t('projects.brief.audience')}
          value={brief.audience ?? ''}
          placeholder="—"
          onSave={async v => { await autoSave({ audience: v }); }}
        />
        <KVRow
          label={t('projects.brief.valueProposition')}
          value={brief.valueProposition ?? ''}
          placeholder="—"
          onSave={async v => { await autoSave({ valueProposition: v }); }}
        />
      </KVGrid>

      {/* Row 3: tone | typography — 2 columns */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 'var(--evo-space-4)', borderBottom: '1px solid var(--evo-stroke)' }}>
        <FieldRow label={t('projects.brief.tone')} last>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
            <Dropdown<string>
              options={toneOptions}
              value={toneDropdownValue}
              onChange={v => {
                if (v === 'custom') {
                  setShowCustomTone(true);
                  if (toneIsPreset) patch({ tone: '' });
                } else {
                  setShowCustomTone(false);
                  void autoSave({ tone: v });
                }
              }}
              placeholder={t('projects.brief.tone')}
              size="sm"
            />
            {showCustomTone && (
              <div onBlur={() => { void serverSave(); }}>
                <Input
                  value={brief.tone ?? ''}
                  onChange={v => patch({ tone: v })}
                  placeholder={t('projects.brief.tones.custom')}
                  size="sm"
                  fullWidth
                  autoFocus
                />
              </div>
            )}
          </div>
        </FieldRow>
        <KVRow
          label={t('projects.brief.typography')}
          value={brief.typography ?? ''}
          placeholder="—"
          onSave={async v => { await autoSave({ typography: v }); }}
          last
        />
      </div>

      {/* Row 4: cta | deadline — 2 columns */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 'var(--evo-space-4)', borderBottom: '1px solid var(--evo-stroke)' }}>
        <FieldRow label={t('projects.brief.cta.label')} last>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
            <Dropdown<string>
              options={ctaOptions}
              value={brief.cta?.type ?? null}
              onChange={v => { void autoSave({ cta: { ...briefRef.current.cta, type: v } }); }}
              placeholder={t('projects.brief.cta.type')}
              size="sm"
            />
            {brief.cta?.type === 'other' && (
              <div onBlur={() => { void serverSave(); }}>
                <Input
                  value={brief.cta?.custom ?? ''}
                  onChange={v => patch({ cta: { ...brief.cta, custom: v } })}
                  placeholder={t('projects.brief.cta.custom')}
                  size="sm"
                  fullWidth
                />
              </div>
            )}
          </div>
        </FieldRow>
        <FieldRow label={t('projects.brief.desiredDeadline')} last>
          <DatePicker
            mode="single"
            value={brief.desiredDeadline ? new Date(brief.desiredDeadline) : null}
            onChange={handleDeadlineChange}
            minDate={minDeadline}
            {...(deadlineError ? { error: deadlineError } : {})}
            size="sm"
          />
        </FieldRow>
      </div>

      {/* Row 5: colors — full width */}
      <FieldRow label={t('projects.brief.colors.label')}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
          <div onBlur={() => { void serverSave(); }}>
            <Textarea
              value={brief.colors?.text ?? ''}
              onChange={v => patch({ colors: { ...brief.colors, text: v } })}
              placeholder={t('projects.brief.colors.text')}
              rows={2}
              fullWidth
              resize="none"
            />
          </div>
          <FileUploadZone
            uploadUrl={uploadUrl}
            value={brief.colors?.attachments ?? []}
            onUploaded={paths => {
              void autoSave({
                colors: { ...briefRef.current.colors, attachments: [...(briefRef.current.colors?.attachments ?? []), ...paths] },
              });
            }}
            onDelete={url => { void handleDeleteAsset(url, 'colors'); }}
            acceptedTypes="image/*,application/pdf"
            dragLabel={t('projects.brief.upload.dragOrClick')}
          />
        </div>
      </FieldRow>

      {/* Row 6: references — full width */}
      <FieldRow label={t('projects.brief.references.label')}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
          {(brief.references?.links ?? []).map(link => (
            <div key={link} style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
              <a
                href={link}
                target="_blank"
                rel="noreferrer"
                style={{
                  flex: 1,
                  fontSize: 'var(--evo-fs-body)',
                  color: 'var(--evo-cyan)',
                  fontFamily: 'var(--evo-font)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {link}
              </a>
              <IconButton
                variant="ghost"
                tone="muted"
                size="sm"
                ariaLabel="remove"
                icon={<svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>}
                onClick={() => removeLink(link)}
              />
            </div>
          ))}
          <div style={{ display: 'flex', gap: 'var(--evo-space-2)' }}>
            <Input
              value={newLink}
              onChange={v => { setNewLink(v); setLinkError(null); }}
              placeholder="https://…"
              size="sm"
              fullWidth
              {...(linkError ? { error: linkError } : {})}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addLink(); } }}
            />
            <Button variant="outline" tone="muted" size="sm" onClick={addLink}>
              {t('projects.brief.references.addLink')}
            </Button>
          </div>
          <FileUploadZone
            uploadUrl={uploadUrl}
            value={brief.references?.attachments ?? []}
            onUploaded={paths => {
              void autoSave({
                references: { ...briefRef.current.references, attachments: [...(briefRef.current.references?.attachments ?? []), ...paths] },
              });
            }}
            onDelete={url => { void handleDeleteAsset(url, 'references'); }}
            acceptedTypes="image/*,application/pdf"
            dragLabel={t('projects.brief.upload.dragOrClick')}
          />
        </div>
      </FieldRow>

      {/* Row 7: logo — full width */}
      <FieldRow label={t('projects.brief.logo')}>
        <FileUploadZone
          uploadUrl={uploadUrl}
          value={brief.logo ? [brief.logo] : []}
          onUploaded={paths => { void autoSave({ logo: paths[0] ?? null }); }}
          onDelete={() => { void autoSave({ logo: null }); }}
          acceptMultiple={false}
          acceptedTypes="image/*,image/svg+xml"
          dragLabel={t('projects.brief.upload.dragOrClick')}
        />
      </FieldRow>

      {/* Row 8: images — full width */}
      <FieldRow label={t('projects.brief.images')}>
        <FileUploadZone
          uploadUrl={uploadUrl}
          value={brief.images ?? []}
          onUploaded={paths => {
            void autoSave({ images: [...(briefRef.current.images ?? []), ...paths] });
          }}
          onDelete={url => { void handleDeleteAsset(url, 'images'); }}
          acceptedTypes="image/*"
          dragLabel={t('projects.brief.upload.dragOrClick')}
        />
      </FieldRow>

      {/* Row 9: notes — full width */}
      <FieldRow label={t('projects.brief.notes')} last>
        <div onBlur={() => { void serverSave(); }}>
          <Textarea
            value={brief.notes ?? ''}
            onChange={v => patch({ notes: v })}
            placeholder={t('projects.brief.notes')}
            rows={4}
            fullWidth
            resize="vertical"
          />
        </div>
      </FieldRow>

      {/* Footer */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--evo-space-2)',
        justifyContent: 'flex-end',
        paddingTop: 'var(--evo-space-4)',
        flexWrap: 'wrap',
      }}>
        <Button variant="ghost" tone="muted" size="sm" loading={isSaving} onClick={() => { void handleSaveDraft(); }}>
          {t('projects.brief.actions.saveDraft')}
        </Button>
        <Button variant="ghost" tone="muted" size="sm" loading={skipBriefMut.isPending} onClick={() => { void handleSkipBrief(); }}>
          {t('projects.brief.actions.skipBrief')}
        </Button>
        <Button
          variant="outline"
          tone="cyan"
          size="sm"
          loading={sendBriefMut.isPending}
          onClick={() => {
            sendBriefMut.mutate(undefined, {
              onSuccess: () => onSendBrief?.(),
            });
          }}
        >
          {t('projects.brief.sendBrief')}
        </Button>
        <Button variant="solid" tone="cyan" size="sm" loading={saveBriefMut.isPending || generateMut.isPending} onClick={() => { void handleGenerateContentPlan(); }}>
          {t('projects.brief.actions.generateContentPlan')}
        </Button>
      </div>
    </DetailSection>
  );
}
